<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="modelValue"
    :modal="true"
    :dismissableMask="false"
    :closeOnEscape="true"
    :style="{ width: '95vw', height: '95vh' }"
    :contentStyle="{ height: 'calc(100% - 60px)', padding: 0 }"
    @update:visible="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="dialog-header">
        <i class="ri-markdown-line"></i>
        <span>{{ $t("dashboardAdmin.blockEditor.editMarkdown") }}</span>
      </div>
    </template>
    <PrimeSplitter class="markdown-editor-splitter" style="height: 100%">
      <PrimeSplitterPanel class="splitter-panel" :size="50" :min-size="10">
        <CodeMirrorBase v-model="localContent" language="markdown" />
      </PrimeSplitterPanel>
      <PrimeSplitterPanel class="splitter-panel" :size="50" :min-size="10">
        <iframe
          :srcdoc="markdownPreview"
          class="markdown-preview-iframe"
          sandbox="allow-scripts allow-popups"
        ></iframe>
      </PrimeSplitterPanel>
    </PrimeSplitter>
    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        icon="ri-close-line"
        text
        @click="handleCancel"
      />
      <PrimeButton
        :label="$t('dashboardAdmin.blockEditor.apply')"
        icon="ri-check-line"
        @click="handleSave"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import { marked } from "marked";
import DOMPurify from "dompurify";
import CodeMirrorBase from "@/components/CodeMirrorBase.vue";

export default {
  name: "MarkdownEditorDialog",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeSplitter: Splitter,
    PrimeSplitterPanel: SplitterPanel,
    CodeMirrorBase,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    content: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "save", "cancel"],
  data() {
    return {
      localContent: this.content,
    };
  },
  computed: {
    markdownPreview() {
      const rawHtml = marked.parse(this.localContent || "");

      // Add a hook to force all links to open in a new tab
      DOMPurify.addHook("afterSanitizeAttributes", function (node) {
        if (node.tagName === "A") {
          node.setAttribute("target", "_blank");
          node.setAttribute("rel", "noopener noreferrer");
        }
      });

      const cleanHtml = DOMPurify.sanitize(rawHtml);

      // Basic CSS for preview
      const styles = `
        body { font-family: sans-serif; color: #333; margin: 1rem; }
        h1, h2, h3 { font-weight: 600; margin-top: 1.5em; margin-bottom: 0.5em; }
        p { margin-bottom: 1em; }
        ul { list-style-type: disc; padding-left: 2em; }
        ol { list-style-type: decimal; padding-left: 2em; }
        blockquote { margin-left: 0; padding-left: 1em; border-left: 3px solid #ccc; color: #666; }
        code { background-color: #eee; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; }
        pre { background-color: #eee; padding: 1em; border-radius: 6px; overflow-x: auto; }
      `;

      return `
        <!DOCTYPE html>
        <html>
          <head>
            <style>${styles}</style>
          </head>
          <body>
            ${cleanHtml}
          </body>
        </html>
      `;
    },
  },
  watch: {
    content(newValue) {
      this.localContent = newValue;
    },
    modelValue(newValue) {
      if (newValue) {
        this.localContent = this.content;
      }
    },
  },
  methods: {
    handleSave() {
      this.$emit("save", this.localContent);
      this.$emit("update:modelValue", false);
    },
    handleCancel() {
      this.localContent = this.content;
      this.$emit("cancel");
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.dialog-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.splitter-panel {
  display: flex;
}

.markdown-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.markdown-editor-splitter :deep(.p-splitter) {
  border: none;
}

.markdown-editor-splitter :deep(.p-splitter-panel) {
  overflow: hidden;
}
</style>
